import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Theme,
  IconButton,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useAppSelector } from '../../app/hooks';
import { selectFeatureFlagHitl3 } from '../../redux/features/config/config.selector';
import Colors from '../../utils/color';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';

export enum UnresolvedItemVariant {
  NOT_ON_MENU = 'notOnMenu',
  INVALID_OPTION = 'invalidOption',
  QUANTITY_ESCALATION = 'quantityEscalation',
  NO_DEFAULT_OPTION = 'noDefaultOption',
  UNMATCHED_COUPON = 'unmatchedCoupon',
}

interface Props {
  variant: UnresolvedItemVariant;
  title: string;
  diagnosticLabel: string;
}

const BACKGROUND_COLOR: Record<UnresolvedItemVariant, string> = {
  [UnresolvedItemVariant.NOT_ON_MENU]: Colors.apricot,
  [UnresolvedItemVariant.INVALID_OPTION]: Colors.beeswax,
  [UnresolvedItemVariant.QUANTITY_ESCALATION]: Colors.spindle,
  [UnresolvedItemVariant.NO_DEFAULT_OPTION]: Colors.teaGreen,
  [UnresolvedItemVariant.UNMATCHED_COUPON]: Colors.mauve,
};

const useStyles = makeStyles<
  Theme,
  { variant: UnresolvedItemVariant; isHitl3: boolean }
>(({ spacing }: Theme) => ({
  container: ({ variant, isHitl3 }) => ({
    // HITL 3.0 reduces the inter-card gap to the shared 8px rhythm.
    marginTop: isHitl3 ? HITL3_PADDING : spacing(2),
    backgroundColor: `${BACKGROUND_COLOR[variant]} !important`,
  }),
  // CardContent = the mainItemname block's parent. HITL 3.0 gives it an even
  // 8px padding on all sides; legacy keeps MUI's default with the tight bottom.
  content: ({ isHitl3 }) =>
    isHitl3
      ? { padding: `${HITL3_PADDING} !important` }
      : { paddingBottom: `${spacing(0.5)} !important` },
  mainItemname: {
    '& h6': {
      lineHeight: '1.57 !important',
      fontSize: `${spacing(1.5)} !important`,
      fontWeight: 'bold !important',
    },
    // HITL 3.0 removes the label block's bottom padding.
    paddingBottom: ({ isHitl3 }) => (isHitl3 ? 0 : spacing(1)),
  },
  diagnosticLabel: {
    fontStyle: 'italic',
  },
  actionsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    '& span': {
      cursor: 'pointer',
    },
  },
  quantity: {
    textAlign: 'center',
    display: 'inline-block',
    minWidth: '40px !important',
    paddingRight: '2px !important',
    paddingLeft: '0px !important',
  },
}));

/**
 * Unified diagnostic card for unresolved items — covers "not on menu",
 * "invalid option", "quantity escalation", "no default option", and
 * "unmatched coupon" variants. Display-only; dismissal is handled by the
 * parent panel's "Dismiss & Repeat" button.
 */
export default function UnresolvedItemCard({
  variant,
  title,
  diagnosticLabel,
}: Props) {
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const classes = useStyles({ variant, isHitl3 });

  return (
    <Card elevation={3} className={classes.container}>
      <CardContent className={classes.content}>
        <div className={classes.mainItemname}>
          <Typography variant="subtitle2">{title}</Typography>
          <Typography variant="subtitle2" className={classes.diagnosticLabel}>
            ({diagnosticLabel})
          </Typography>
        </div>
        {variant === UnresolvedItemVariant.NOT_ON_MENU && (
          <div className={classes.actionsWrapper}>
            <div>
              <IconButton size="small" disabled={true}>
                <RemoveIcon />
              </IconButton>
              <span className={classes.quantity}>1</span>
              <IconButton size="small" disabled={true}>
                <AddIcon />
              </IconButton>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
